function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _classPrivateFieldGet(receiver, privateMap) { var descriptor = _classExtractFieldDescriptor(receiver, privateMap, "get"); return _classApplyDescriptorGet(receiver, descriptor); }

function _classApplyDescriptorGet(receiver, descriptor) { if (descriptor.get) { return descriptor.get.call(receiver); } return descriptor.value; }

function _classPrivateFieldSet(receiver, privateMap, value) { var descriptor = _classExtractFieldDescriptor(receiver, privateMap, "set"); _classApplyDescriptorSet(receiver, descriptor, value); return value; }

function _classExtractFieldDescriptor(receiver, privateMap, action) { if (!privateMap.has(receiver)) { throw new TypeError("attempted to " + action + " private field on non-instance"); } return privateMap.get(receiver); }

function _classApplyDescriptorSet(receiver, descriptor, value) { if (descriptor.set) { descriptor.set.call(receiver, value); } else { if (!descriptor.writable) { throw new TypeError("attempted to set read only private field"); } descriptor.value = value; } }

/* eslint-disable max-depth */
import ContractImpl from 'web3-eth-contract';
import { toHex, toChecksumAddress, isAddress, isHexStrict, utf8ToHex, toBN } from 'web3-utils';
import { AddChainError, ChainNotConfiguredError, ConnectorNotFoundError, ResourceUnavailableError, SwitchChainError, UserRejectedRequestError } from '../errors';
import { getInjectedName, normalizeChainId } from '../utils';
import { Connector } from './base';
import { RpcErrorCode } from '../constants';

var _provider = /*#__PURE__*/new WeakMap();

export class InjectedConnector extends Connector {
  constructor({
    chains,
    options = {
      shimDisconnect: true
    }
  } = {}) {
    super({
      chains,
      options
    });

    _defineProperty(this, "id", void 0);

    _defineProperty(this, "name", void 0);

    _defineProperty(this, "ready", typeof window != 'undefined' && !!window.ethereum);

    _provider.set(this, {
      writable: true,
      value: void 0
    });

    _defineProperty(this, "switchingChains", void 0);

    _defineProperty(this, "contract", void 0);

    _defineProperty(this, "onAccountsChanged", accounts => {
      if (accounts.length === 0) this.emit('disconnect');else {
        this.emit('accountsChanged', {
          account: toChecksumAddress(accounts[0])
        });
        this.emit('change', {
          account: toChecksumAddress(accounts[0])
        });
      }
    });

    _defineProperty(this, "onChainChanged", chainId => {
      const id = normalizeChainId(chainId);
      const unsupported = this.isChainUnsupported(id);
      this.emit('change', {
        chain: {
          id,
          unsupported
        }
      });
      this.emit('chainChanged', {
        chain: {
          id,
          unsupported
        }
      });
    });

    _defineProperty(this, "onDisconnect", () => {
      var _this$options;

      // We need this as MetaMask can emit the "disconnect" event
      // upon switching chains. This workaround ensures that the
      // user currently isn't in the process of switching chains.
      if ((_this$options = this.options) !== null && _this$options !== void 0 && _this$options.shimChainChangedDisconnect && this.switchingChains) {
        this.switchingChains = false;
        return;
      }

      this.emit('disconnect');
    });

    let name = 'Injected';
    const overrideName = options.name;
    if (typeof overrideName === 'string') name = overrideName;else if (typeof window !== 'undefined') {
      const detectedName = getInjectedName(window.ethereum);
      if (overrideName) name = overrideName(detectedName);else name = typeof detectedName === 'string' ? detectedName : detectedName[0];
    }
    this.id = 'injected';
    this.name = name;
  }

  async connect(options) {
    try {
      const {
        chainId
      } = options || {};
      const provider = await this.getProvider();
      if (!provider) throw new ConnectorNotFoundError();

      if (provider.on) {
        provider.on('accountsChanged', this.onAccountsChanged);
        provider.on('chainChanged', this.onChainChanged);
        provider.on('disconnect', this.onDisconnect);
      }

      this.emit('message', {
        type: 'connecting'
      });
      const account = await this.getAccount(); // Switch to chain if provided

      let id = await this.getChainId();
      let unsupported = this.isChainUnsupported(id);

      if (chainId && id !== chainId) {
        const chain = await this.switchChain(chainId);
        id = chain.id;
        unsupported = this.isChainUnsupported(id);
      }

      return {
        account,
        chain: {
          id,
          unsupported
        },
        provider
      };
    } catch (error) {
      if (this.isUserRejectedRequestError(error)) throw new UserRejectedRequestError(error);
      if (error.code === RpcErrorCode.RESOURCE_UNAVAILABLE) throw new ResourceUnavailableError(error);
      throw error;
    }
  }

  async disconnect() {
    const provider = await this.getProvider();
    if (!(provider !== null && provider !== void 0 && provider.removeListener)) return;
    provider.removeListener('accountsChanged', this.onAccountsChanged);
    provider.removeListener('chainChanged', this.onChainChanged);
    provider.removeListener('disconnect', this.onDisconnect);
  }

  async getAccount() {
    const provider = await this.getProvider();
    if (!provider) throw new ConnectorNotFoundError();
    const accounts = await provider.request({
      method: 'eth_requestAccounts'
    }); // return checksum address

    return toChecksumAddress(accounts[0]);
  }

  async getChainId() {
    const provider = await this.getProvider();
    if (!provider) throw new ConnectorNotFoundError();
    return await provider.request({
      method: 'eth_chainId'
    }).then(normalizeChainId);
  }

  async getProvider() {
    if (typeof window !== 'undefined' && !!window.ethereum) _classPrivateFieldSet(this, _provider, window.ethereum);
    return _classPrivateFieldGet(this, _provider);
  }

  async getContract(config) {
    const {
      address,
      contractInterface
    } = config || {};
    const provider = await this.getProvider();
    if (!provider) throw new ConnectorNotFoundError();
    ContractImpl.setProvider(provider); //@ts-expect-error hack because web3 library typedef doesn't match its JS implementation

    return new ContractImpl(contractInterface, address);
  }

  async isAuthorized() {
    try {
      const provider = await this.getProvider();
      if (!provider) throw new ConnectorNotFoundError();
      const accounts = await provider.request({
        method: 'eth_accounts'
      });
      const account = accounts[0];
      return !!account;
    } catch {
      return false;
    }
  }

  async switchChain(chainId) {
    var _this$options2;

    if ((_this$options2 = this.options) !== null && _this$options2 !== void 0 && _this$options2.shimChainChangedDisconnect) this.switchingChains = true;
    const provider = await this.getProvider();
    if (!provider) throw new ConnectorNotFoundError();
    const id = toHex(chainId);

    try {
      await provider.request({
        method: 'wallet_switchEthereumChain',
        params: [{
          chainId: id
        }]
      });
      return this.chains.find(x => x.id === chainId) ?? {
        id: chainId,
        name: `Chain ${id}`,
        network: `${id}`,
        rpcUrls: {
          default: ''
        }
      };
    } catch (error) {
      var _data, _data$originalError;

      const chain = this.chains.find(x => x.id === chainId);
      if (!chain) throw new ChainNotConfiguredError(); // Indicates chain is not added to provider

      if (error.code === RpcErrorCode.CHAIN_NOT_FOUND || // Unwrapping for MetaMask Mobile
      // https://github.com/MetaMask/metamask-mobile/issues/2944#issuecomment-976988719
      (error === null || error === void 0 ? void 0 : (_data = error.data) === null || _data === void 0 ? void 0 : (_data$originalError = _data.originalError) === null || _data$originalError === void 0 ? void 0 : _data$originalError.code) === RpcErrorCode.CHAIN_NOT_FOUND) {
        try {
          var _chain$blockExplorers;

          await provider.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: id,
              chainName: chain.name,
              nativeCurrency: chain.nativeCurrency,
              rpcUrls: [chain.rpcUrls.default],
              blockExplorerUrls: (_chain$blockExplorers = chain.blockExplorers) !== null && _chain$blockExplorers !== void 0 && _chain$blockExplorers.default ? [chain.blockExplorers.default.url] : undefined
            }]
          });
          return chain;
        } catch (addError) {
          if (this.isUserRejectedRequestError(addError)) throw new UserRejectedRequestError(error);
          throw new AddChainError();
        }
      }

      if (this.isUserRejectedRequestError(error)) throw new UserRejectedRequestError(error);
      throw new SwitchChainError(error);
    }
  }

  async watchAsset({
    address,
    decimals = 18,
    image,
    symbol
  }) {
    const provider = await this.getProvider();
    if (!provider) throw new ConnectorNotFoundError();
    return await provider.request({
      method: 'wallet_watchAsset',
      params: {
        type: 'ERC20',
        options: {
          address,
          decimals,
          image,
          symbol
        }
      }
    });
  }

  async signMessage(args) {
    try {
      const {
        address,
        message
      } = args;
      const provider = await this.getProvider();
      if (!provider) throw new ConnectorNotFoundError();
      const signature = await provider.request({
        method: 'personal_sign',
        params: [!isAddress(message) && isHexStrict(message) ? message : utf8ToHex(message), address, '']
      });
      return signature;
    } catch (error) {
      if (error.code === RpcErrorCode.USER_REJECTED_REQUEST) throw new UserRejectedRequestError(error);
      throw error;
    }
  }

  async fetchBalance(address) {
    const provider = await this.getProvider();
    if (!provider) throw new ConnectorNotFoundError();
    const balance = await provider.request({
      method: 'eth_getBalance',
      params: [address, 'latest']
    });
    return toBN(balance === null || balance === void 0 ? void 0 : balance.toString());
  }

  isUserRejectedRequestError(error) {
    return error.code === RpcErrorCode.USER_REJECTED_REQUEST;
  }

}